import axios from 'axios'
import { ElMessage } from 'element-plus'

// 创建axios实例
const request = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:8005/api/v1',
  timeout: 10000
})

// 请求拦截器
request.interceptors.request.use(
  config => {
    // 添加JWT令牌到请求头
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  response => {
    return response.data
  },
  error => {
    let message = '请求失败'
    if (error.response) {
      message = error.response.data.message || `请求失败 ${error.response.status}`
    } else if (error.request) {
      message = '网络连接失败'
    }
    ElMessage.error(message)
    return Promise.reject(error)
  }
)

// API接口定义
const api = {
  // 账户相关
  getAccounts: (params) => request.get('/accounts', { params }),
  getAccount: (id) => request.get(`/accounts/${id}`),
  createAccount: (data) => request.post('/accounts', data),
  updateAccount: (id, data) => request.put(`/accounts/${id}`, data),
  reorderAccounts: (orders) => request.post('/accounts/reorder', orders),
  getAccountBalance: (id, params) => request.get(`/accounts/${id}/balance`, { params }),
  getAccountTransactions: (id, params) => request.get(`/accounts/${id}/transactions`, { params }),
  getAccountMonthlyStats: (id, params) => request.get(`/accounts/${id}/monthly-stats`, { params }),
  toggleAccountHidden: (id, isHidden) => request.patch(`/accounts/${id}/hidden`, null, { params: { is_hidden: isHidden } }),
  toggleAccountExcludeNetWorth: (id, exclude) => request.patch(`/accounts/${id}/exclude-net-worth`, null, { params: { exclude } }),
  getAccountCategoryConfig: (accountType) => request.get('/accounts/config/categories', { params: accountType ? { account_type: accountType } : {} }),
  getBanks: () => request.get('/accounts/config/banks'),
  
  // 账户分类配置管理
  getAllAccountCategories: (includeInactive) => request.get('/accounts/config/categories/all', { params: { include_inactive: includeInactive || false } }),
  createAccountCategory: (accountType, subcategoryName, displayOrder) => request.post('/accounts/config/categories', null, { 
    params: { account_type: accountType, subcategory_name: subcategoryName, display_order: displayOrder || 0 } 
  }),
  updateAccountCategory: (id, data) => request.put(`/accounts/config/categories/${id}`, null, { params: data }),
  deleteAccountCategory: (id) => request.delete(`/accounts/config/categories/${id}`),

  // 交易记录相关
  getTransactions: (params) => request.get('/transactions', { params }),
  getTransaction: (id) => request.get(`/transactions/${id}`),
  createTransaction: (data) => request.post('/transactions', data),
  updateTransaction: (id, data) => request.put(`/transactions/${id}`, data),
  deleteTransaction: (id) => request.delete(`/transactions/${id}`),

  // 账单导入
  previewBillImport: (formData) => request.post('/imports/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000
  }),
  getImportBatches: () => request.get('/imports/batches'),
  getImportBatch: (id) => request.get(`/imports/batches/${id}`),
  confirmBillImport: (id, data) => request.post(`/imports/batches/${id}/confirm`, data, { timeout: 60000 }),
  revertBillImport: (id) => request.post(`/imports/batches/${id}/revert`, null, { timeout: 60000 }),
  deletePendingBillImport: (id) => request.delete(`/imports/batches/${id}`),

  // AI记账
  analyzeAIAccounting: (formData) => request.post('/ai-accounting/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 90000
  }),

  // 分类相关 - 新增树形结构接口
  getExpenseCategoriesTree: (includeHidden = false) => request.get('/categories/expense/tree', { params: { include_hidden: includeHidden } }),
  getIncomeCategoriesTree: (includeHidden = false) => request.get('/categories/income/tree', { params: { include_hidden: includeHidden } }),
  getExpenseMajorCategories: () => request.get('/categories/expense/major'),
  getExpenseMinorCategories: (majorCategoryId) => request.get('/categories/expense/minor', {
    params: majorCategoryId ? { major_category_id: majorCategoryId } : {}
  }),
  getIncomeMajorCategories: () => request.get('/categories/income/major'),
  getIncomeMinorCategories: (majorCategoryId) => request.get('/categories/income/minor', {
    params: majorCategoryId ? { major_category_id: majorCategoryId } : {}
  }),
  
  // 分类 CRUD
  createExpenseMajorCategory: (data) => request.post('/categories/expense/major', data),
  createExpenseMinorCategory: (data) => request.post('/categories/expense/minor', data),
  createIncomeMajorCategory: (data) => request.post('/categories/income/major', data),
  updateExpenseMajorCategory: (id, data) => request.put(`/categories/expense/major/${id}`, data),
  updateExpenseMinorCategory: (id, data) => request.put(`/categories/expense/minor/${id}`, data),
  updateIncomeMajorCategory: (id, data) => request.put(`/categories/income/major/${id}`, data),
  deleteExpenseMajorCategory: (id) => request.delete(`/categories/expense/major/${id}`),
  deleteExpenseMinorCategory: (id) => request.delete(`/categories/expense/minor/${id}`),
  deleteIncomeMajorCategory: (id) => request.delete(`/categories/income/major/${id}`),
  
  // 分类隐藏
  toggleExpenseMajorHidden: (id, isHidden) => request.patch(`/categories/expense/major/${id}/hidden`, { is_hidden: isHidden }),
  toggleExpenseMinorHidden: (id, isHidden) => request.patch(`/categories/expense/minor/${id}/hidden`, { is_hidden: isHidden }),
  toggleIncomeMajorHidden: (id, isHidden) => request.patch(`/categories/income/major/${id}/hidden`, { is_hidden: isHidden }),

  // 用户认证相关
  login: (data) => request.post('/auth/login', data),
  register: (data) => request.post('/auth/register', data),
  logout: () => request.post('/auth/logout'),
  getCurrentUser: () => request.get('/auth/me'),
  updateCurrentUser: (data) => request.put('/auth/me', data),

  // 报表分析
  getReportSummary: (params) => request.get('/analytics/reports/summary', { params })
}

export default api 
